import type { Language } from '../i18n/language';

export interface LegalSection {
  title: string;
  paragraphs?: string[];
  bullets?: string[];
}

export interface LegalDocument {
  path: string;
  title: string;
  seoTitle: string;
  seoDescription: string;
  lastUpdated: string;
  intro: string[];
  sections: LegalSection[];
}

interface LegalContentMap {
  privacy: LegalDocument;
  terms: LegalDocument;
  accountDeletion: LegalDocument;
  support: LegalDocument;
}

const supportEmail = 'support@englishbylevels.com';
const lastUpdatedTr = '24 Temmuz 2026';
const lastUpdatedEn = 'July 24, 2026';
const privacyLastUpdatedTr = '25 Temmuz 2026';
const privacyLastUpdatedEn = 'July 25, 2026';

export const legalContact = {
  supportEmail,
};

export const legalContentByLanguage: Record<Language, LegalContentMap> = {
  tr: {
    privacy: {
      path: '/privacy',
      title: 'Gizlilik Politikası',
      seoTitle: 'Gizlilik Politikası',
      seoDescription:
        'English By Levels iOS ve Android mobil uygulaması ile resmi web sitesi için gizlilik politikası.',
      lastUpdated: privacyLastUpdatedTr,
      intro: [
        'Bu Gizlilik Politikası, English By Levels ("English By Levels", "biz", "bize", "bizim") tarafından iOS ve Android mobil uygulaması ile englishbylevels.com resmi web sitesini kullanırken kişisel verilerin nasıl toplandığını, kullanıldığını, saklandığını ve korunduğunu açıklar.',
        'Yapay zeka sağlayıcılarıyla içerik paylaşımı, bu politikanın gösterilmesinden ayrı olarak uygulama içinde açık izninize sunulur.',
      ],
      sections: [
        {
          title: '1. Toplayabileceğimiz Veri Türleri',
          bullets: [
            'Hesap verileri: ad, e-posta adresi, profil bilgileri, dahili hesap kimlikleri ve hesap tercihleri.',
            'İsteğe bağlı profil bilgileri: cinsiyet, doğum tarihi, ülke ve şehir. Bu bilgiler uygulamanın temel öğrenme özelliklerini kullanmak için zorunlu değildir. Bu alanları atlayabilir ve eksik bilgileri daha sonra Hesap Ayarları üzerinden ekleyebilirsiniz.',
            'Kimlik doğrulama verileri: Google ile Giriş ve Apple ile Giriş üzerinden alınan kullanıcı kimliği, e-posta (paylaşıldığında) ve temel profil bilgileri.',
            'Öğrenme etkinliği verileri: seçilen seviyeler, ilerleme, kaydedilen içerikler, quiz sonuçları, yazma analizleri, çeviri/analiz istekleri, bildirim ve hatırlatıcı tercihleri ile özellik kullanım bilgileri.',
            'Abonelik ve ödeme durumu verileri: plan durumu, yenileme durumu ve App Store/Google Play tarafından sağlanan işlem referansları. Tam kart numarası saklamayız.',
            'Analitik, teşhis ve çökme verileri: uygulama sürümü, cihaz modeli, işletim sistemi, dil, performans ve hata kayıtları.',
            'Kullanıcı tarafından gönderilen yazı, çeviri, hikaye analizi, nimonik veya geri bildirim içerikleri.',
          ],
        },
        {
          title: '2. Mikrofon ve Konuşma Özellikleri',
          paragraphs: [
            'Konuşma veya telaffuz özelliklerini kullandığınızda mikrofon erişimi yalnızca konuşma değerlendirmesi ve geri bildirim üretimi amacıyla kullanılır.',
            'Konuşma girdileri, özelliği sağlamak amacıyla güvenli konuşma işleme servisleri tarafından işlenebilir. Mikrofon verileri reklam amacıyla kullanılmaz.',
          ],
        },
        {
          title: '3. Yapay Zeka Destekli Özellikler',
          paragraphs: [
            'Çeviri özelliğinde yazdığınız metin veya konuşmadan dönüştürülen metin; yazma analizinde yazınız ve seçilen konu bağlamı; nimonik üretiminde kelime, anlam ve görsel istemi işlenir.',
            'Bu içerikler, istenen çıktıyı üretmek amacıyla birincil sağlayıcı olarak OpenAI tarafından işlenir. OpenAI kullanılamazsa metin işlemleri için Anthropic, nimonik görsel üretimi için Cloudflare Workers AI yedek sağlayıcı olarak kullanılabilir.',
            'Ad, e-posta, cinsiyet, doğum tarihi, ülke ve şehir profil alanlarını bu sağlayıcılara kendiliğimizden göndermeyiz. Ancak gönderdiğiniz metne kişisel bilgi eklerseniz bu bilgi metnin parçası olarak işlenebilir.',
            'İlk yapay zeka veri aktarımından önce uygulama gönderilecek verileri ve sağlayıcıları açıklar ve izninizi ister. İzin vermezseniz veri aktarılmaz ve ilgili yapay zeka özelliği çalışmaz. Verdiğiniz izni uygulamadaki Ayarlar bölümünden geri çekebilirsiniz.',
            'Yapay zeka destekli çıktılar öğrenmeyi desteklemek içindir; her durumda kesinlik veya hatasızlık garantisi verilmez.',
          ],
        },
        {
          title: '4. Verileri Nasıl Kullanırız',
          bullets: [
            'Hesap oluşturma ve yönetimi için.',
            'Öğrenme, konuşma, çeviri, analiz, nimonik ve kişiselleştirme özelliklerini sunmak ve geliştirmek için.',
            'Cinsiyet, yaş aralığı, ülke ve şehir bilgilerini kişiselleştirme ve toplulaştırılmış kullanıcı kitlesi analizleri için kullanmak amacıyla.',
            'Doğum tarihini kullanıcının yaş grubunu belirlemek ve yaşa uygun reklam kurallarını uygulamak için kullanmak amacıyla. Doğum tarihi paylaşılmazsa güvenli reklam kısıtlamaları uygulanır.',
            'Abonelik erişimini, kredi haklarını, kullanım limitlerini ve nimonik paketlerini yönetmek için.',
            'Bildirimleri ve hatırlatıcıları kullanıcı tercihleri ile cihaz izinlerine göre çalıştırmak için.',
            'Kötüye kullanımı tespit etmek, güvenliği sağlamak ve sorunları gidermek için.',
            'Hizmet veya politika güncellemeleri hakkında iletişim kurmak için.',
          ],
        },
        {
          title: '5. Üçüncü Taraf Servisler',
          paragraphs: [
            'Hizmeti işletmek için Apple, Google, bulut altyapı sağlayıcıları, analitik araçları, çökme raporlama servisleri, bildirim servisleri ve destek araçları gibi üçüncü taraflardan yararlanabiliriz.',
            'Yapay zeka özelliklerinde OpenAI birincil sağlayıcıdır; Anthropic metin, Cloudflare Workers AI ise nimonik görsel üretimi için yalnızca gerekli olduğunda yedek sağlayıcı olarak kullanılabilir.',
            'Bu sağlayıcılar verileri hizmeti sunmak, güvenliği sağlamak ve kötüye kullanımı önlemek için kendi ticari şartları ve gizlilik politikaları kapsamında işleyebilir. Sağlayıcılardan verileri bu politika ve uygulanabilir veri koruma kurallarıyla uyumlu biçimde korumalarını ve yalnızca hizmeti sunmak için işlemelerini isteriz.',
            'Doğum tarihi, yaş grubunu belirlemek için kendi hizmetimizde kullanılır ve reklam sağlayıcısına bir profil alanı olarak gönderilmez.',
          ],
        },
        {
          title: '6. Veri Saklama ve Silme',
          paragraphs: [
            'Kişisel veriler, hesap işletimi, destek, güvenlik ve yasal yükümlülükler için gerekli süre kadar saklanır.',
            'Hesabınızı uygulama içinden sildiğinizde veya silme talebi verdiğinizde, hesaba bağlı veriler makul süre içinde silinir veya anonimleştirilir; ancak yasal, muhasebesel, dolandırıcılık önleme ve uyuşmazlık yönetimi gerekleri kapsamındaki sınırlı veriler geçici olarak tutulabilir.',
          ],
        },
        {
          title: '7. Kullanıcı Hakları',
          bullets: [
            'Kişisel verilerinize erişim talep etme.',
            'Hatalı verilerin düzeltilmesini talep etme.',
            'Hesap ve ilişkili kişisel verilerin silinmesini talep etme.',
            'Uygulanabilir hukuka göre belirli işleme faaliyetlerine itiraz etme.',
            'Gizlilikle ilgili soru veya şikayet iletme.',
          ],
        },
        {
          title: '8. İletişim',
          paragraphs: [
            `Gizlilik ve veri talepleri için: ${supportEmail}`,
          ],
        },
      ],
    },
    terms: {
      path: '/terms',
      title: 'Kullanım Koşulları',
      seoTitle: 'Kullanım Koşulları',
      seoDescription:
        'English By Levels mobil uygulaması ve resmi web sitesi için kullanım koşulları.',
      lastUpdated: lastUpdatedTr,
      intro: [
        'Bu Kullanım Koşulları, English By Levels mobil uygulaması, ilgili servisleri ve englishbylevels.com web sitesi kullanımınızı düzenler.',
        'Hesap oluşturarak veya hizmeti kullanarak bu koşulları kabul etmiş olursunuz.',
      ],
      sections: [
        {
          title: '1. Hizmet Tanımı',
          paragraphs: [
            'English By Levels; seviye bazlı okuma, kelime çalışması, cümle pratiği, gramer, soru, yazma analizi, çeviri, hikaye analizi, nimonik ve abonelik tabanlı premium özellikler dahil İngilizce öğrenme araçları sunar.',
          ],
        },
        {
          title: '2. Hesap Sorumluluğu',
          bullets: [
            'Doğru hesap bilgisi sağlamak ve kimlik bilgilerini korumak sizin sorumluluğunuzdadır.',
            'Hesabınız üzerinden yapılan işlemlerden siz sorumlusunuz.',
            'Hizmeti kullanmanız bulunduğunuz yargı alanında hukuken uygun olmalıdır.',
          ],
        },
        {
          title: '3. Kabul Edilemez Kullanım',
          bullets: [
            'Hizmeti kötüye kullanamaz, bozamaz, tersine mühendislik yapamaz veya yetkisiz erişim girişiminde bulunamazsınız.',
            'Hizmeti hukuka aykırı, zararlı veya dolandırıcılık amaçlı kullanamazsınız.',
            'Hukuku veya üçüncü taraf haklarını ihlal eden içerik yükleyemezsiniz.',
          ],
        },
        {
          title: '4. Yapay Zeka Destekli Özellikler',
          paragraphs: [
            'Uygulama; çeviri, yazma analizi, hikaye analizi, nimonik üretimi ve benzeri alanlarda yapay zeka destekli çıktılar sunabilir.',
            'Bu çıktılar öğrenmeyi desteklemek amacıyla sağlanır; her durumda kesinlik, eksiksizlik veya hatasızlık garantisi verilmez.',
            'Kullanıcı, bu çıktıları değerlendirirken kendi karar ve sorumluluğuyla hareket eder; uygunsuz, hukuka aykırı veya üçüncü kişi haklarını ihlal eden içerik göndermemelidir.',
          ],
        },
        {
          title: '5. Nimonik İçerikler ve İçerik Koruması',
          bullets: [
            'Nimonik görselleri ve metinleri özel koruma kapsamındadır.',
            'İçeriklerin izinsiz kopyalanması, çoğaltılması, toplu dışa aktarılması, yeniden yayınlanması veya satılması yasaktır.',
            'Ekran görüntüsü, ekran kaydı veya üçüncü kişilerle paylaşım gibi ihlaller nimonik erişiminin geçici durdurulması, özellik kısıtlaması veya hesabın askıya alınmasıyla sonuçlanabilir.',
          ],
        },
        {
          title: '6. Abonelik, Faturalandırma ve Yenileme',
          paragraphs: [
            'Ücretli abonelikler platformunuza göre Apple App Store veya Google Play üzerinden satın alınır ve yönetilir.',
            'Abonelikler, bir sonraki fatura dönemi öncesinde iptal edilmezse platform kurallarına göre otomatik yenilenebilir.',
            'Fiyatlar ve planlar zamanla değişebilir. Fiyat değişiklikleri ilgili mağaza politikalarına göre uygulanır.',
            'Plus aboneliği premium erişim ve nimonik paketi satın alma hakkı sağlayabilir. Pro aboneliği Plus haklarına ek olarak aylık 20 nimonik üretim hakkı sağlayabilir.',
            'Abonelik kaynaklı dönemsel nimonik kredileri bir sonraki döneme devretmez; abonelik sona erdiğinde, yenilenemediğinde veya pasif duruma düştüğünde bu haklar sıfırlanabilir.',
            'Nimonik paketleri yalnız aktif abonelik bulunan hesaplar için satın alınabilir. Paket satın alımıyla edinilen nimonik kredileri abonelikten bağımsızdır ve geçerli mevzuat ile platform kuralları saklı kalmak üzere hesapta korunur.',
          ],
        },
        {
          title: '7. Bildirimler, Hatırlatıcılar ve Limitler',
          paragraphs: [
            'Bildirimler ve hatırlatıcılar kullanıcı tercihleri ve cihaz izinleri doğrultusunda çalışır.',
            'Bildirimden başlatılan quizler, kelime pratikleri ve diğer özellikler planınıza göre limitlere tabi olabilir.',
            'Ücretsiz, Plus ve Pro kullanım limitleri hizmetin sürdürülebilirliği ve platform gereksinimleri doğrultusunda zaman içinde güncellenebilir.',
          ],
        },
        {
          title: '8. Fesih ve Hesap Kapatma',
          paragraphs: [
            'Hizmeti dilediğiniz zaman bırakabilir; uygulama içi ayarlardan veya destek üzerinden hesap silme talep edebilirsiniz.',
            'Koşul ihlali, kötüye kullanım, güvenlik riski veya yasal zorunluluk durumunda erişimi askıya alabilir veya sonlandırabiliriz.',
          ],
        },
        {
          title: '9. Sorumluluk Sınırı',
          paragraphs: [
            'Hizmet, yasal olarak izin verilen en geniş kapsamda "olduğu gibi" ve "mevcut olduğu ölçüde" sunulur.',
            'English By Levels; dolaylı, arızi, özel veya sonuçsal zararlardan sorumlu tutulamaz.',
          ],
        },
        {
          title: '10. Koşullarda Değişiklik',
          paragraphs: [
            'Bu koşullar zaman zaman güncellenebilir. Güncellemeler sonrası hizmeti kullanmaya devam etmeniz yeni koşulları kabul ettiğiniz anlamına gelir.',
          ],
        },
        {
          title: '11. İletişim',
          paragraphs: [
            `Kullanım koşullarıyla ilgili sorular için: ${supportEmail}`,
          ],
        },
      ],
    },
    accountDeletion: {
      path: '/account-deletion',
      title: 'Hesap Silme',
      seoTitle: 'Hesap Silme',
      seoDescription:
        'English By Levels hesabının nasıl silineceği ve silme sonrası verilerin nasıl işlendiği.',
      lastUpdated: lastUpdatedTr,
      intro: [
        'Bu sayfa, English By Levels hesabınızı nasıl sileceğinizi ve silme sonrası verilerin nasıl işlendiğini açıklar.',
        'Google Play dahil mağaza gereksinimleri için herkese açık bir kaynaktır.',
      ],
      sections: [
        {
          title: '1. Uygulama İçinden Hesap Silme',
          bullets: [
            'English By Levels uygulamasını açın ve hesabınıza giriş yapın.',
            'Hesap Ayarları bölümüne gidin.',
            'Hesabı Sil seçeneğine dokunun ve işlemi onaylayın.',
          ],
        },
        {
          title: '2. Hesap Silinince Silinen Veriler',
          bullets: [
            'Hesap profili ve giriş bağlantıları.',
            'Hesabınıza bağlı öğrenme ilerlemesi, kayıtlı içerikler, yazma/çeviri/analiz kayıtları ve kullanıcıya bağlı öğrenme verileri.',
            'Hesaba bağlı uygulama tercihleri.',
          ],
        },
        {
          title: '3. Geçici Olarak Tutulabilecek Veriler',
          bullets: [
            'Vergi, muhasebe ve yasal uyum için gerekli işlem/faturalandırma kayıtları.',
            'Güvenlik, dolandırıcılık önleme veya uyuşmazlık çözümü için sınırlı süre tutulan kayıtlar.',
          ],
        },
        {
          title: '4. Aktif Abonelikler',
          paragraphs: [
            'Hesap silme işlemi App Store veya Google Play aboneliklerini otomatik iptal etmez.',
            'Silme işleminden önce, gelecekteki yenilemeleri engellemek için aboneliğinizi Apple/Google hesap ayarlarından iptal edin.',
          ],
        },
        {
          title: '5. Destek',
          paragraphs: [
            `Hesabınıza erişemiyorsanız veya silme desteği istiyorsanız: ${supportEmail}`,
          ],
        },
      ],
    },
    support: {
      path: '/support',
      title: 'Destek',
      seoTitle: 'Destek',
      seoDescription:
        'English By Levels için giriş, abonelik, hesap silme ve genel destek iletişim sayfası.',
      lastUpdated: lastUpdatedTr,
      intro: [
        'English By Levels ile ilgili yardıma mı ihtiyacınız var? En kısa sürede destek oluruz.',
        `Destek e-postası: ${supportEmail}`,
      ],
      sections: [
        {
          title: '1. Giriş Sorunları',
          bullets: [
            'Google ile Giriş veya Apple ile Giriş problemleri.',
            'Giriş sonrası beklenen hesaba erişememe durumu.',
          ],
        },
        {
          title: '2. Abonelik Sorunları',
          bullets: [
            'Satın alma sonrası premium erişimin görünmemesi.',
            'Yenileme, iptal veya fatura durumu soruları.',
            'Nimonik kredileri, paket satın alma ve limitlerle ilgili sorular.',
          ],
        },
        {
          title: '3. Hesap Silme Talepleri',
          bullets: [
            'Uygulama içinden hesap silme adımlarında yardım.',
            'Hesaba erişim olmadan silme desteği.',
          ],
        },
        {
          title: '4. Genel İletişim',
          paragraphs: [
            `Diğer tüm sorular ve geri bildirimler için: ${supportEmail}`,
          ],
        },
      ],
    },
  },
  en: {
    privacy: {
      path: '/privacy',
      title: 'Privacy Policy',
      seoTitle: 'Privacy Policy',
      seoDescription:
        'Privacy Policy for the English By Levels iOS and Android mobile application and official website.',
      lastUpdated: privacyLastUpdatedEn,
      intro: [
        'This Privacy Policy explains how English By Levels ("English By Levels", "we", "us", "our") collects, uses, stores, and protects personal information when you use the English By Levels mobile application on iOS and Android and our official website at englishbylevels.com.',
        'Sharing content with AI providers is presented for your explicit in-app permission separately from displaying this policy.',
      ],
      sections: [
        {
          title: '1. Data We May Collect',
          bullets: [
            'Account data: name, email address, profile information, internal account identifiers, and account preferences.',
            'Optional profile information: gender, birth date, country, and city. This information is not required to use the app’s core learning features. You may skip these fields and add any missing information later from Account Settings.',
            'Authentication data: sign-in information received from Google Sign-In and Apple Sign-In (for example, provider user ID, email when shared, and basic profile attributes).',
            'Learning activity data: selected levels, progress, saved items, quiz results, writing analyses, translation/analysis requests, notification and reminder preferences, and feature usage in the app.',
            'Subscription and billing status data: current plan status, renewal state, and transaction references provided by App Store and Google Play. We do not receive or store full payment card numbers.',
            'Technical analytics and diagnostics: app version, device model, operating system, language, crash logs, and performance diagnostics.',
            'User-submitted writing, translation, story analysis, mnemonic, or feedback content.',
          ],
        },
        {
          title: '2. Microphone and Speech Features',
          paragraphs: [
            'When you use speaking or pronunciation features, microphone access is used only to capture audio needed to evaluate speech and return feedback.',
            'Speech inputs may be processed by secure speech-processing providers to deliver the feature. We do not use microphone data for advertising.',
          ],
        },
        {
          title: '3. AI-Assisted Features',
          paragraphs: [
            'For translation, we process text you type or text transcribed from speech; for writing analysis, your writing and selected prompt context; and for mnemonic generation, the word, meaning, and image prompt.',
            'This content is processed by OpenAI as the primary provider to generate the requested output. If OpenAI is unavailable, Anthropic may be used as the fallback text provider and Cloudflare Workers AI as the fallback mnemonic image provider.',
            'We do not intentionally send your name, email, gender, birth date, country, or city profile fields to these providers. Personal information you include in submitted text may be processed as part of that text.',
            'Before the first AI data transfer, the app identifies the data and providers and asks for your permission. If you do not allow it, no data is transferred and the related AI feature will not run. You can withdraw permission later from Settings in the app.',
            'AI-assisted outputs are provided to support learning; accuracy, completeness, or error-free results are not guaranteed.',
          ],
        },
        {
          title: '4. How We Use Data',
          bullets: [
            'To create and manage your account.',
            'To provide and improve learning, speech, translation, analysis, mnemonic, and personalization features.',
            'To use gender, age range, country, and city information for personalization and aggregated audience analytics.',
            'To use birth date to determine the user’s age group and apply age-appropriate advertising rules. If birth date is not provided, safe advertising restrictions are applied.',
            'To maintain subscriptions, credit rights, usage limits, and mnemonic package access.',
            'To operate notifications and reminders according to user preferences and device permissions.',
            'To detect abuse, secure the service, and debug failures.',
            'To communicate important service or policy updates.',
          ],
        },
        {
          title: '5. Third-Party Services',
          paragraphs: [
            'We may use third-party providers such as Apple, Google, cloud hosting providers, analytics tools, crash reporting services, notification services, and customer support tools to operate the service.',
            'For AI features, OpenAI is the primary provider; Anthropic may be used as the fallback text provider and Cloudflare Workers AI as the fallback mnemonic image provider only when needed.',
            'These providers may process data under their commercial terms and privacy policies to deliver the service, maintain security, and prevent abuse. We require providers to protect data consistently with this policy and applicable data-protection requirements and to process it only to provide the service.',
            'Birth date is used by our service to determine an age group and is not sent to the advertising provider as a profile field.',
          ],
        },
        {
          title: '6. Data Retention and Deletion',
          paragraphs: [
            'We keep personal data only as long as necessary for account operation, support, legal obligations, and security purposes.',
            'If you request deletion or delete your account in-app, we delete or anonymize account-linked content within a reasonable period, except where limited retention is required for legal, fraud prevention, accounting, or dispute handling purposes.',
          ],
        },
        {
          title: '7. Your Rights',
          bullets: [
            'Request access to your personal data.',
            'Request correction of inaccurate information.',
            'Request deletion of your account and associated personal data.',
            'Object to certain processing where applicable by law.',
            'Contact us with privacy questions or complaints.',
          ],
        },
        {
          title: '8. Contact',
          paragraphs: [
            `For privacy or data requests, contact us at ${supportEmail}.`,
          ],
        },
      ],
    },
    terms: {
      path: '/terms',
      title: 'Terms of Use',
      seoTitle: 'Terms of Use',
      seoDescription:
        'Terms of Use for English By Levels mobile application and official website.',
      lastUpdated: lastUpdatedEn,
      intro: [
        'These Terms of Use govern your access to and use of the English By Levels mobile application, related services, and website at englishbylevels.com.',
        'By creating an account or using the service, you agree to these Terms.',
      ],
      sections: [
        {
          title: '1. Service Description',
          paragraphs: [
            'English By Levels provides English learning content and tools, including level-based reading, vocabulary practice, sentence practice, grammar, questions, writing analysis, translation, story analysis, mnemonics, and subscription-based premium features.',
          ],
        },
        {
          title: '2. Account Responsibilities',
          bullets: [
            'You are responsible for providing accurate account information and keeping your credentials secure.',
            'You are responsible for activity performed through your account.',
            'You must be legally allowed to use the service in your jurisdiction.',
          ],
        },
        {
          title: '3. Acceptable Use',
          bullets: [
            'Do not misuse, disrupt, reverse engineer, or attempt unauthorized access to the service.',
            'Do not use the service for unlawful, harmful, or fraudulent activity.',
            'Do not upload or submit content that violates law or third-party rights.',
          ],
        },
        {
          title: '4. AI-Assisted Features',
          paragraphs: [
            'The app may provide AI-assisted outputs for translation, writing analysis, story analysis, mnemonic generation, and similar features.',
            'These outputs are provided to support learning; accuracy, completeness, or error-free results are not guaranteed.',
            'Users should evaluate these outputs with their own judgment and responsibility, and must not submit unlawful, inappropriate, or third-party-rights-infringing content.',
          ],
        },
        {
          title: '5. Mnemonic Content and Content Protection',
          bullets: [
            'Mnemonic images and texts are under special protection.',
            'Unauthorized copying, duplication, bulk export, republication, or sale of content is prohibited.',
            'Violations such as screenshots, screen recordings, or sharing with third parties may result in temporary suspension of mnemonic access, feature restrictions, or account suspension.',
          ],
        },
        {
          title: '6. Subscriptions, Billing, and Renewals',
          paragraphs: [
            'Paid subscriptions are purchased and managed through Apple App Store or Google Play, depending on your platform.',
            'Subscriptions may automatically renew unless canceled before the next billing cycle in your platform account settings.',
            'Pricing and available plans may change over time. Any price changes are handled according to applicable store policies.',
            'The Plus subscription may provide premium access and mnemonic pack purchase access. The Pro subscription may add 20 monthly mnemonic generation credits on top of Plus benefits.',
            'Subscription-based periodic mnemonic credits do not carry over to the next period; they may reset when the subscription ends, fails to renew, or becomes inactive.',
            'Mnemonic packages may be purchased only by accounts with an active subscription. Mnemonic credits obtained through package purchase are independent from subscription and remain in the account, subject to applicable law and platform rules.',
          ],
        },
        {
          title: '7. Notifications, Reminders, and Limits',
          paragraphs: [
            'Notifications and reminders operate according to user preferences and device permissions.',
            'Quizzes, word practices, and other features started from notifications may be subject to limits depending on your plan.',
            'Free, Plus, and Pro usage limits may be updated over time for service sustainability and platform requirements.',
          ],
        },
        {
          title: '8. Cancellation, Termination, and Account Closure',
          paragraphs: [
            'You may stop using the service at any time and may request account deletion through in-app settings or support.',
            'We may suspend or terminate access for violations of these Terms, abuse, security risks, or legal requirements.',
          ],
        },
        {
          title: '9. Limitation of Liability',
          paragraphs: [
            'To the maximum extent permitted by law, the service is provided on an "as is" and "as available" basis without warranties of uninterrupted or error-free operation.',
            'English By Levels is not liable for indirect, incidental, special, consequential, or punitive damages arising from your use of the service.',
          ],
        },
        {
          title: '10. Changes to These Terms',
          paragraphs: [
            'We may update these Terms from time to time. Continued use of the service after updates means you accept the revised Terms.',
          ],
        },
        {
          title: '11. Contact',
          paragraphs: [
            `For questions about these Terms, contact ${supportEmail}.`,
          ],
        },
      ],
    },
    accountDeletion: {
      path: '/account-deletion',
      title: 'Account Deletion',
      seoTitle: 'Account Deletion',
      seoDescription:
        'How to delete your English By Levels account and what happens to your data after deletion.',
      lastUpdated: lastUpdatedEn,
      intro: [
        'This page explains how to delete your English By Levels account and what happens to your data after deletion.',
        'It is a public resource for app store compliance, including Google Play requirements.',
      ],
      sections: [
        {
          title: '1. How to Delete Your Account In-App',
          bullets: [
            'Open the English By Levels app and sign in.',
            'Go to Account Settings.',
            'Tap Delete Account and confirm the deletion request.',
          ],
        },
        {
          title: '2. Data Deleted After Account Deletion',
          bullets: [
            'Your account profile and sign-in association.',
            'Saved learning progress, saved content, writing/translation/analysis records, and user-generated learning data linked to your account.',
            'App-specific preferences tied to your account.',
          ],
        },
        {
          title: '3. Data That May Be Retained Temporarily',
          bullets: [
            'Transaction and billing records required for tax, accounting, and legal compliance.',
            'Security, fraud-prevention, or dispute-resolution logs retained for a limited period.',
          ],
        },
        {
          title: '4. Active Subscriptions',
          paragraphs: [
            'Deleting your account does not automatically cancel App Store or Google Play subscriptions.',
            'Before deletion, cancel active subscriptions in your Apple or Google account subscription settings to avoid future renewals.',
          ],
        },
        {
          title: '5. Need Help',
          paragraphs: [
            `If you cannot access your account or need deletion support, email ${supportEmail}.`,
          ],
        },
      ],
    },
    support: {
      path: '/support',
      title: 'Support',
      seoTitle: 'Support',
      seoDescription:
        'Contact English By Levels support for sign-in, subscription, account deletion, and general questions.',
      lastUpdated: lastUpdatedEn,
      intro: [
        'Need help with English By Levels? Contact us and we will help you as quickly as possible.',
        `Support email: ${supportEmail}`,
      ],
      sections: [
        {
          title: '1. Sign-In Issues',
          bullets: [
            'Problems with Google Sign-In or Apple Sign-In.',
            'Not receiving expected account access after login.',
          ],
        },
        {
          title: '2. Subscription Issues',
          bullets: [
            'Premium access not reflected after purchase.',
            'Renewal, cancellation, or billing status questions.',
            'Questions about mnemonic credits, package purchases, and limits.',
          ],
        },
        {
          title: '3. Account Deletion Requests',
          bullets: [
            'Need help deleting an account from inside the app.',
            'Cannot access the account but still need deletion assistance.',
          ],
        },
        {
          title: '4. General Contact',
          paragraphs: [
            `For all other questions, feature feedback, or partnership inquiries, email ${supportEmail}.`,
          ],
        },
      ],
    },
  },
};
